import React from 'react';
import { Link } from 'react-router-dom';
import { GardenLogo, SplashHero } from '../../components/Illustrations';
import { Meta } from '../../components/Atoms';

/**
 * AuthShell - wspólny layout dla wszystkich ekranów auth flow w Terroir.
 *
 * Forest dark + cream + sage. Hero illustration po lewej (desktop) / na górze (mobile).
 * Form po prawej (desktop) / pod heroem (mobile).
 *
 * Props:
 *   - eyebrow: małe mono uppercase nad tytułem
 *   - title: duży Fraunces
 *   - subtitle: opis pod tytułem
 *   - children: form
 *   - footer: linki na dole (Login/Register/Forgot itp.)
 *   - illustration: opcjonalne (default SplashHero)
 */

export default function AuthShell({ eyebrow, title, subtitle, children, footer, illustration }) {
  return (
    <div className="redesign-root" style={{
      minHeight: 'var(--t-vh)',
      background: 'var(--t-bg)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Top bar - logo + ewentualnie language switcher */}
      <header style={{
        padding: '20px 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        maxWidth: 1200, margin: '0 auto', width: '100%',
      }}>
        <Link to="/login" style={{ textDecoration: 'none' }}>
          <GardenLogo size={32} withText textSize={22} />
        </Link>
        <Link
          to="/pomoc"
          style={{
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.18em', textTransform: 'uppercase',
            color: 'var(--t-text-mute)', textDecoration: 'none',
          }}
        >Poradnik →</Link>
      </header>

      {/* Main content - split mobile column / desktop side by side */}
      <main style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        maxWidth: 1200,
        margin: '0 auto',
        width: '100%',
        padding: '20px 24px 40px',
      }}>
        <style>{`
          @media (min-width: 1024px) {
            .auth-grid {
              grid-template-columns: 1fr 1fr !important;
              gap: 80px;
              align-items: center;
              padding: 60px 40px !important;
            }
          }
        `}</style>

        <div className="auth-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 40,
          width: '100%',
        }}>
          {/* Left/top - illustration + headline */}
          <div className="t-stagger" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}>
            {illustration || <SplashHero size={180} />}
            {eyebrow && <Meta color="sage" style={{ marginTop: 24 }}>{eyebrow}</Meta>}
            <div style={{
              fontFamily: 'var(--t-display)', fontWeight: 300,
              fontSize: 'clamp(40px, 7vw, 64px)',
              lineHeight: 0.95, letterSpacing: '-.03em',
              color: 'var(--t-text)', marginTop: 16,
              textWrap: 'balance',
            }}>{title}</div>
            {subtitle && (
              <div style={{
                marginTop: 18, fontSize: 16, lineHeight: 1.55,
                color: 'var(--t-text-dim)', maxWidth: 420,
              }}>{subtitle}</div>
            )}
          </div>

          {/* Right/bottom - form */}
          <div style={{
            display: 'flex', flexDirection: 'column',
            justifyContent: 'center',
            maxWidth: 440,
            width: '100%',
            margin: '0 auto',
          }}>
            <div style={{
              padding: '32px 28px',
              borderRadius: 18,
              background: 'var(--t-bg-elev)',
              border: '1px solid var(--t-line)',
            }}>
              {children}
            </div>

            {footer && (
              <div style={{
                marginTop: 18, padding: '0 8px',
                textAlign: 'center',
              }}>
                {footer}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// ── Wspólne style inputów ───────────────────────────────────────
export const authInputStyle = {
  width: '100%',
  padding: '14px 16px',
  borderRadius: 12,
  background: 'var(--t-bg)',
  border: '1px solid var(--t-line-2)',
  color: 'var(--t-text)',
  fontFamily: 'var(--t-sans)',
  fontSize: 16,
  letterSpacing: '-.01em',
  outline: 'none',
  boxSizing: 'border-box',
};

// ── Label z mono caps ────────────────────────────────────────────
export function AuthLabel({ children, htmlFor }) {
  return (
    <label htmlFor={htmlFor} style={{
      display: 'block', marginBottom: 8,
      fontFamily: 'var(--t-mono)', fontSize: 10,
      letterSpacing: '.22em', textTransform: 'uppercase',
      color: 'var(--t-text-dim)',
    }}>{children}</label>
  );
}

// ── Error banner ────────────────────────────────────────────────
export function AuthError({ children }) {
  if (!children) return null;
  return (
    <div style={{
      padding: '12px 14px',
      borderRadius: 10,
      background: 'rgba(192, 106, 74, 0.08)',
      borderLeft: '3px solid var(--t-rust)',
      fontSize: 13, color: 'var(--t-text)',
      marginBottom: 14,
    }}>{children}</div>
  );
}

// ── Success banner ──────────────────────────────────────────────
export function AuthSuccess({ children }) {
  if (!children) return null;
  return (
    <div style={{
      padding: '12px 14px',
      borderRadius: 10,
      background: 'rgba(168, 196, 158, 0.08)',
      borderLeft: '3px solid var(--t-sage)',
      fontSize: 13, color: 'var(--t-text)',
      marginBottom: 14,
    }}>{children}</div>
  );
}
